import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "../../../generated/app-service-gql";
import {
  CurrentUserQuery,
  UpdateCurrentUserMutation,
  UpdateCurrentUserMutationVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { CURRENT_USER_QUERY_KEY } from "./useCurrentUser";

const UPDATE_USER_MUTATION = graphql(`
  mutation updateCurrentUser($request: UpdateCurrentUserRequest!) {
    updateCurrentUser(user: $request) {
      guid
      firstName
      lastName
      jobTitle
      mainGoal
    }
  }
`);

/**
 * API which allows the current user to update simple fields about themselves such as first name, last name main goal etc.
 */
export function useUpdateCurrentUser(
  options?: UseMutationOptions<
    UpdateCurrentUserMutation,
    unknown,
    UpdateCurrentUserMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  return useGraphQlMutation(UPDATE_USER_MUTATION, {
    ...options,
    onSuccess: (data, variables, context) => {
      const { guid, firstName, lastName, jobTitle } = data.updateCurrentUser;
      // The mutation returns the plain User type, which doesn't carry email preferences.
      const sendAppActivityNotifications =
        variables.request.sendAppActivityNotifications ?? undefined;
      queryClient.setQueryData<CurrentUserQuery>(CURRENT_USER_QUERY_KEY, (prev) => {
        if (!prev || prev.currentUser.guid !== guid) return prev;
        return {
          currentUser: {
            ...prev.currentUser,
            firstName,
            lastName,
            jobTitle,
            ...(sendAppActivityNotifications !== undefined && { sendAppActivityNotifications }),
          },
        };
      });
      // Member lists show the user's name.
      void queryClient.invalidateQueries({ queryKey: ["users"] });

      options?.onSuccess?.(data, variables, context);
    },
  });
}
